import { Box } from '@mui/material';
import { BaseSyntheticEvent, ReactElement, ReactNode, useState } from 'react';
import { Control } from 'react-hook-form';

import useAuth from 'portal/src/auth/useAuth';
import { BUTTON_TEXT } from 'portal/src/constants/button-text';

import ConfirmCancelDialog from './ConfirmCancelDialog';
import FormButtonColumn, {
  ExtraButtonConfig,
  FormButtonLayout,
} from './FormButtonColumn';
import { AllFormFieldTypes } from './FormFieldTypes';
import FormView from './FormView';

export const FormContainer = ({
  afterMainFields,
  backButtonlabel = BUTTON_TEXT.back,
  beforeMainFields,
  buttonLayout = 'side',
  hideCancelButton = false,
  columnNumber = 2,
  control,
  isDirty,
  extraButtons,
  header,
  handleBackAction,
  handleSubmit,
  isDeactivated = false,
  isNew,
  isPending,
  removeContainerStyle = false,
  isSubmitDisabled = false,
  allowReadOnlyEdit = false,
  mainFieldsConfig,
  maxWidth,
  resetForm,
  saveOrUpdateLabel,
}: {
  afterMainFields?: ReactNode;
  backButtonlabel?: string;
  beforeMainFields?: ReactNode;
  buttonLayout?: FormButtonLayout;
  columnNumber?: 1 | 2;
  control: Control<any>;
  hideCancelButton?: boolean;
  isDirty: boolean;
  extraButtons?: ExtraButtonConfig[];
  header?: ReactElement | string;
  handleBackAction?: () => void;
  handleSubmit: (e?: BaseSyntheticEvent<object, any, any>) => Promise<void>;
  isDeactivated?: boolean;
  isNew: boolean;
  isPending: boolean;
  removeContainerStyle?: boolean;
  isSubmitDisabled?: boolean;
  allowReadOnlyEdit?: boolean;
  mainFieldsConfig: AllFormFieldTypes[];
  maxWidth?: number | string;
  resetForm?: () => Promise<void>;
  saveOrUpdateLabel?: string;
}) => {
  const { hasReadOnlyAccess } = useAuth();
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const isReadOnly = hasReadOnlyAccess() && !allowReadOnlyEdit;

  const handleBackClick = async () => {
    if (handleBackAction) {
      handleBackAction();
    } else if (resetForm) {
      await resetForm();
      setShowCancelDialog(false);
    }
  };

  const handleCancelClick = () => {
    if (isDirty) {
      setShowCancelDialog(true);
    } else {
      handleBackClick();
    }
  };

  const isBottomButtonLayout = buttonLayout === 'bottom';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isBottomButtonLayout ? 'column' : 'row',
        gap: 2,
        width: '100%',
        maxWidth,
        mx: maxWidth ? 'auto' : undefined,
        backgroundColor: 'background.paper',
        borderRadius: 1,
        p: removeContainerStyle ? 0 : 2,
        border: removeContainerStyle ? 'none' : '1px solid',
        borderColor: removeContainerStyle ? 'transparent' : 'divider',
        height: isBottomButtonLayout ? 'auto' : '100%',
      }}
    >
      <FormView
        header={header}
        beforeMainFields={beforeMainFields}
        mainFieldsConfig={mainFieldsConfig}
        afterMainFields={afterMainFields}
        columnNumber={columnNumber}
        control={control}
        isDeactivated={isDeactivated}
        isReadOnly={isReadOnly}
      />
      <FormButtonColumn
        backButtonlabel={backButtonlabel}
        buttonLayout={buttonLayout}
        extraButtons={isBottomButtonLayout ? undefined : extraButtons}
        hideCancelButton={hideCancelButton}
        isDirty={isDirty}
        isSubmitDisabled={isSubmitDisabled}
        isNew={isNew}
        isPending={isPending}
        isReadOnly={isReadOnly}
        saveOrUpdateLabel={saveOrUpdateLabel}
        handleBackClick={resetForm ? undefined : handleBackClick}
        handleCancelClick={handleCancelClick}
        handleSubmit={handleSubmit}
      />
      <ConfirmCancelDialog
        showDialog={showCancelDialog}
        onConfirmCancel={handleBackClick}
        onKeepFormState={() => setShowCancelDialog(false)}
      />
    </Box>
  );
};
